<template>
    <v-container class="evaluations-list-view" fluid>
      <v-card class="mb-6 page-header-card" elevation="2">
        <v-card-title>
          <v-icon left large color="primary">mdi-file-document-multiple-outline</v-icon>
          Evaluations
        </v-card-title>
        <v-card-text>
          <p>Manage and complete Evaluation Reports for students.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
              color="primary"
              outlined
              @click="filterByStatus = ''"
              :class="{ 'active-filter': filterByStatus === '' }"
          >
            All Evaluations
          </v-btn>
          <v-btn
              color="grey"
              outlined
              @click="filterByStatus = 'DRAFT'"
              :class="{ 'active-filter': filterByStatus === 'DRAFT' }"
          >
            Drafts
          </v-btn>
          <v-btn
              color="blue"
              outlined
              @click="filterByStatus = 'IN_PROGRESS'"
              :class="{ 'active-filter': filterByStatus === 'IN_PROGRESS' }"
          >
            In Progress
          </v-btn>
          <v-btn
              color="orange"
              outlined
              @click="filterByStatus = 'SUBMITTED'"
              :class="{ 'active-filter': filterByStatus === 'SUBMITTED' }"
          >
            Submitted
          </v-btn>
          <v-btn
              color="success"
              outlined
              @click="filterByStatus = 'APPROVED'"
              :class="{ 'active-filter': filterByStatus === 'APPROVED' }"
          >
            Approved
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>

      <div class="actions-row mb-4">
        <v-text-field
            v-model="searchQuery"
            prepend-inner-icon="mdi-magnify"
            label="Search by matric/staff ID..."
            outlined
            dense
            hide-details
            class="search-field"
        ></v-text-field>

        <v-select
            v-model="selectedSession"
            :items="sessionOptions"
            item-text="name"
            item-value="id"
            label="Session"
            outlined
            dense
            hide-details
            class="session-filter ml-4"
            style="max-width: 200px;"
            @change="fetchEvaluations"
            :loading="isLoadingSessions"
            return-object
        ></v-select>

        <v-select
            v-model="selectedTemplate"
            :items="templateFilterOptions"
            item-text="name"
            item-value="id"
            label="Template"
            outlined
            dense
            hide-details
            clearable
            class="ml-4"
            style="max-width: 220px;"
            @change="fetchEvaluations"
            :loading="isLoadingTemplates"
        ></v-select>

        <v-btn color="primary" v-if="canCreateNewEvaluation" class="ml-4" @click="showNewEvaluationDialog = true">
          <v-icon left>mdi-plus</v-icon>
          New Evaluation
        </v-btn>

        <!-- Download Department Report Button (for authorized roles) -->
        <v-btn
            v-if="canDownloadDepartmentReport"
            color="info"
            outlined
            class="ml-2"
            :loading="isDownloadingReport"
            @click="showDownloadDialog = true"
        >
          <v-icon left>mdi-download</v-icon>
          Department Report
        </v-btn>
      </div>

      <!-- Loading Indicator -->
      <div v-if="isLoading" class="text-center my-8">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="mt-4">Loading evaluations...</p>
      </div>

      <!-- No Evaluations Message -->
      <v-card v-else-if="filteredEvaluations.length === 0" class="text-center pa-6" outlined>
        <v-icon size="64" color="grey lighten-1">mdi-file-document-outline</v-icon>
        <h3 class="mt-4 text-h5 grey--text text--darken-1">No evaluations found</h3>
      </v-card>

      <!-- Evaluations List -->
      <div v-else class="evaluations-grid">
        <v-hover v-for="evaluation in filteredEvaluations" :key="evaluation.id" v-slot:default="{ hover }">
          <v-card
              class="evaluation-card mb-4"
              :elevation="hover ? 8 : 2"
              :class="{ 'on-hover': hover }"
              @click="openEvaluation(evaluation.id)"
          >
            <div class="status-indicator" :class="getStatusClass(evaluation.status)"></div>
            <v-card-title class="evaluation-title">
              {{ evaluation.title }}
            </v-card-title>

            <v-card-text>
              <div class="evaluation-details">
                <div class="detail-item">
                  <v-icon small color="grey darken-1" class="mr-1">mdi-account</v-icon>
                  <span class="detail-label">Evaluatee:</span>
                  <span class="detail-value">{{ evaluation.evaluateeName }}</span>
                </div>

                <!--              <div class="detail-item">-->
                <!--                <v-icon small color="grey darken-1" class="mr-1">mdi-account-tie</v-icon>-->
                <!--                <span class="detail-label">Evaluator:</span>-->
                <!--                <span class="detail-value">{{ evaluation.evaluatorName }}</span>-->
                <!--              </div>-->

                <div class="detail-item">
                  <v-icon small color="grey darken-1" class="mr-1">mdi-calendar-range</v-icon>
                  <span class="detail-label">Period:</span>
                  <span class="detail-value">{{ formatDate(evaluation.startDate) }} - {{ formatDate(evaluation.endDate) }}</span>
                </div>

                <div class="detail-item">
                  <v-icon small color="grey darken-1" class="mr-1">mdi-file-document-outline</v-icon>
                  <span class="detail-label">Template:</span>
                  <span class="detail-value">{{ evaluation.templateName }}</span>
                </div>
              </div>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions>
              <v-chip small :color="getStatusColor(evaluation.status)" outlined>
                {{ evaluation.status }}
              </v-chip>
              <v-spacer></v-spacer>
              <v-btn
                  text
                  color="primary"
                  @click.stop="openEvaluation(evaluation.id)"
              >
                <v-icon small left>mdi-eye</v-icon>
                View
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-hover>
      </div>

      <!-- New Evaluation Dialog -->
      <v-dialog v-model="showNewEvaluationDialog" max-width="600px">
        <v-card>
          <v-card-title>
            <span class="text-h5">Create New Evaluation</span>
          </v-card-title>
          <v-card-text>
            <v-form ref="form" v-model="isFormValid">
<!--              <v-text-field-->
<!--                  v-model="newEvaluation.title"-->
<!--                  label="Evaluation Title"-->
<!--                  required-->
<!--                  :rules="[v => !!v || 'Title is required']"-->
<!--              ></v-text-field>-->

              <v-select
                  v-model="newEvaluation.templateId"
                  label="Select Template"
                  :items="templates"
                  item-text="name"
                  item-value="id"
                  required
                  :rules="[v => !!v || 'Template is required']"
                  :loading="isLoadingTemplates"
                  :disabled="isLoadingTemplates"
              ></v-select>

              <v-select
                  v-model="newEvaluation.evaluateeId"
                  label="Student (You)"
                  :items="students"
                  item-text="name"
                  item-value="id"
                  required
                  :rules="[v => !!v || 'Evaluatee is required']"
                  :loading="isLoadingStaff"
                  :disabled="isLoadingStaff"
              ></v-select>

              <v-select
                  v-model="newEvaluation.session"
                  :items="sessionOptions"
                  item-text="name"
                  item-value="id"
                  label="Session"
                  required
                  :rules="[v => !!v || 'Session is required']"
                  :loading="isLoadingSessions"
                  return-object
              ></v-select>

              <div class="date-fields">
                <v-menu
                    v-model="startDateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        v-model="newEvaluation.startDate"
                        label="Start Date"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        required
                        :rules="[v => !!v || 'Start date is required']"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                      v-model="newEvaluation.startDate"
                      @input="startDateMenu = false"
                  ></v-date-picker>
                </v-menu>

                <v-menu
                    v-model="endDateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        v-model="newEvaluation.endDate"
                        label="End Date"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        required
                        :rules="[
                      v => !!v || 'End date is required',
                      v => !v || !newEvaluation.startDate || v >= newEvaluation.startDate || 'End date must be after start date'
                    ]"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                      v-model="newEvaluation.endDate"
                      @input="endDateMenu = false"
                      :min="newEvaluation.startDate"
                  ></v-date-picker>
                </v-menu>
              </div>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="showNewEvaluationDialog = false">
              Cancel
            </v-btn>
            <v-btn
                color="primary"
                @click="createNewEvaluation"
                :disabled="!isFormValid || isCreatingEvaluation || isLoadingTemplates || isLoadingStaff"
                :loading="isCreatingEvaluation"
            >
              Create
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Department Report Download Dialog -->
      <v-dialog v-model="showDownloadDialog" max-width="500">
        <v-card>
          <v-card-title class="headline">
            <v-icon left color="info">mdi-download</v-icon>
            Download Department Report
          </v-card-title>
          <v-card-text>
            <v-form ref="downloadForm">
              <v-select
                  v-model="downloadTemplate"
                  :items="templates"
                  item-text="name"
                  item-value="id"
                  label="Select Template"
                  outlined
                  dense
                  :rules="[v => !!v || 'Template is required']"
                  :loading="isLoadingTemplates"
              ></v-select>
              <v-select
                  v-model="downloadSession"
                  :items="sessionOptions"
                  item-text="name"
                  item-value="id"
                  label="Select Session (Optional)"
                  outlined
                  dense
                  clearable
                  :loading="isLoadingSessions"
                  return-object
              ></v-select>
              <div class="text-caption text--secondary mt-2">
                Note: The report will be generated for your department{{ userDepartmentName ? ` (${userDepartmentName})` : '' }}.
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showDownloadDialog = false">
              Cancel
            </v-btn>
            <v-btn
                color="info"
                @click="downloadReport"
                :loading="isDownloadingReport"
                :disabled="!downloadTemplate"
            >
              <v-icon left>mdi-download</v-icon>
              Download
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
</template>

<script>
import {
  getAllEvaluations,
  createEvaluation,
  downloadDepartmentEvaluationReport
} from '@/shared/apis/aper/aperEvaluations';
import { getActiveTemplatesApi } from '@/shared/apis/aper/aperTemplate';
import Session from '@/shared/apis/session';
import Roles from "@/shared/Roles";
import Api from "@/main/api";
import { mapGetters } from 'vuex';

export default {
  name: 'EvaluationsListView',
  components: {  },
  data() {
    return {
      evaluations: [],
      isLoading: false,
      searchQuery: '',
      filterByStatus: '',
      selectedSession: null,
      selectedTemplate: null,
      sessionOptions: [],
      isLoadingSessions: false,
      searchDebounceTimer: null,
      showNewEvaluationDialog: false,
      isFormValid: false,
      isCreatingEvaluation: false,

      // Loading states for templates and staff
      isLoadingTemplates: false,
      isLoadingStaff: false,

      // Date pickers
      startDateMenu: false,
      endDateMenu: false,

      // New evaluation form data
      newEvaluation: {
        title: '',
        templateId: '',
        evaluateeId: '',
        evaluatorId: '',
        startDate: '',
        endDate: '',
        session: null
      },

      // API data for templates and staff
      templates: [],
      students: [],

      // Download functionality
      showDownloadDialog: false,
      downloadTemplate: null,
      downloadSession: null,
      isDownloadingReport: false
    }
  },
  computed: {
    ...mapGetters(['getUser']),
    filteredEvaluations() {
      let filtered = this.evaluations;

      // Filter by status (client-side since status filter buttons are instant)
      if (this.filterByStatus) {
        filtered = filtered.filter(evaluation => evaluation.status === this.filterByStatus);
      }

      return filtered;
    },
    templateFilterOptions() {
      return this.templates;
    },
    canCreateEvaluation() {
      return this.getUser?.roles?.includes(Roles.Admin) ||
        this.getUser?.roles?.includes(Roles.Hod);
    },
    availableTemplates() {
      return this.templates.filter(template => template.active);
    },
    availableStaffMembers() {
      return this.students.filter(staff => staff.active);
    },
    canDownloadDepartmentReport() {
      return this.getUser?.roles?.includes(Roles.Admin) ||  this.getUser?.user?.roles?.includes(Roles.Admin) ||
        this.getUser?.roles?.includes(Roles.Hod) || this.getUser?.user?.roles?.includes(Roles.Hod) ||
        this.getUser?.roles?.includes(Roles.DeanSpgs) || this.getUser?.user?.roles?.includes(Roles.SecretarySpgs) ||
        this.getUser?.roles?.includes(Roles.Dean) || this.getUser?.user?.roles?.includes(Roles.Dean);
    },
    userDepartmentName() {
      return this.getUser?.department?.name;
    },

    canCreateNewEvaluation(){
      return this.getUser?.roles?.includes(Roles.DeanSpgs) || this.getUser?.user?.roles?.includes(Roles.DeanSpgs);
    }
  },
  watch: {
    searchQuery() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.fetchEvaluations();
      }, 500);
    }
  },
  created() {
    this.fetchSessions();
    this.fetchEvaluations();
    this.fetchTemplates();
    this.fetchStaffMembers();
  },
  beforeDestroy() {
    clearTimeout(this.searchDebounceTimer);
  },
  methods: {
    async fetchSessions() {
      this.isLoadingSessions = true;
      try {
        const currentUser = this.getUser;

        if (currentUser.roles && Roles.contains(currentUser?.roles, Roles.Student)) {
          // For students, fetch their paid sessions
          const response = await Session.getFullPaidSessionsForStudent(currentUser.userId);
          this.sessionOptions = response.data || [];

          // Set current session as default if available
          if (this.sessionOptions.length > 0) {
            this.selectedSession = this.sessionOptions[0];
          }
        } else {
          // For staff, fetch all sessions for their programme type(s)
          // First try to get their programme type from profile
          let programmeTypeId = this.$store.getters.getCurrentProgrammeType?.id;

          if (programmeTypeId) {
            const response = await Api.session.allFor(programmeTypeId);
            this.sessionOptions = response.data || [];
          } else {
            // Fallback to all sessions if no programme type
            // const response = await Session.all();
            this.sessionOptions = [];
          }

          // Set most recent session as default
          if (this.sessionOptions.length > 0) {
            this.selectedSession = this.sessionOptions[0];
          }
        }
      } catch (error) {
        console.error('Error fetching sessions:', error);
        this.$toast?.error('Failed to fetch sessions');
      } finally {
        this.isLoadingSessions = false;
      }
    },
    async fetchEvaluations() {
      this.isLoading = true;
      try {
        const currentUser = this.getUser?.user?.id ?? this.getUser?.id;

        if (currentUser && this.selectedSession) {
          const filters = {
            userId: currentUser,
            sessionId: this.selectedSession.id
          };
          if (this.selectedTemplate) filters.templateId = this.selectedTemplate;
          if (this.searchQuery) filters.student = this.searchQuery;

          const response = await getAllEvaluations(filters);
          this.evaluations = response.data || [];
        } else {
          const errorMsg = currentUser ? 'User ID is missing' : 'No user object found';
          this.$emit('error', 'User not properly authenticated: ' + errorMsg);
          this.evaluations = [];
        }
      } catch (error) {
        this.$emit('error', 'Failed to load evaluations: ' + (error.response?.data?.message || error.message));
        this.evaluations = [];
      } finally {
        this.isLoading = false;
      }
    },
    async fetchTemplates() {
      this.isLoadingTemplates = true;
      try {
        const response = await getActiveTemplatesApi();
        this.templates = response.data.map((template) => ({
          id: template.id.toString(),
          name: template.name,
          active: template.active
        }));
      } catch (error) {
        console.error('Error fetching templates:', error);
        this.$toast.error('Failed to fetch templates');
      } finally {
        this.isLoadingTemplates = false;
      }
    },
    async fetchStaffMembers() {
      this.isLoadingStaff = true;
      try {
        // const response = await this.$api.staff.all();
        this.students = [this.getUser];
      } catch (error) {
        console.log(error)
        this.$toast.error('Failed to fetch student');
      } finally {
        this.isLoadingStaff = false;
      }
    },
    openNewEvaluationDialog() {
      this.showNewEvaluationDialog = true;
      this.resetNewEvaluationForm();
    },
    resetNewEvaluationForm() {
      this.newEvaluation = {
        title: '',
        templateId: '',
        evaluateeId: '',
        evaluatorId: '',
        startDate: '',
        endDate: '',
        session: this.selectedSession // Default to current selected session
      };
    },
    async createNewEvaluation() {
      if (!this.isFormValid) return;

      this.isCreatingEvaluation = true;
      try {
        const evaluationData = {
          ...this.newEvaluation,
          sessionId: this.newEvaluation.session?.id
        };

        // Remove the session object and keep only sessionId
        delete evaluationData.session;

        await createEvaluation(evaluationData);
        this.$toast.success('Evaluation created successfully');
        this.showNewEvaluationDialog = false;
        this.fetchEvaluations();
      } catch (error) {
        console.error('Error creating evaluation:', error);
        this.$toast.error('Failed to create evaluation');
      } finally {
        this.isCreatingEvaluation = false;
      }
    },
    viewEvaluation(evaluation) {
      this.$router.push(`/evaluation/evaluations/${evaluation.id}`);
    },
    openEvaluation(evaluationId) {
      this.$router.push(`/evaluation/evaluations/${evaluationId}`);
    },
    getStatusClass(status) {
      return `status-${status.toLowerCase().replace('_', '-')}`;
    },
    getStatusColor(status) {
      const colors = {
        'DRAFT': 'grey',
        'IN_PROGRESS': 'blue',
        'SUBMITTED': 'orange',
        'APPROVED': 'green',
        'REJECTED': 'red'
      };
      return colors[status] || 'grey';
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString();
    },
    openDownloadDialog() {
      this.showDownloadDialog = true;
      this.downloadSession = this.selectedSession;
    },
    async downloadReport() {
      if (!this.downloadTemplate) return;

      this.isDownloadingReport = true;
      try {
        const response = await downloadDepartmentEvaluationReport(
          this.downloadTemplate,
          null, // departmentId - let backend use user's department
          this.downloadSession?.id || null
        );

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const templateName = this.templates.find(t => t.id === this.downloadTemplate)?.name || 'report';
        const sessionName = this.downloadSession?.name || '';
        const fileName = sessionName
          ? `department_evaluation_report_${templateName}_${sessionName}.pdf`
          : `department_evaluation_report_${templateName}.pdf`;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.$toast.success('Report downloaded successfully');
        this.showDownloadDialog = false;
      } catch (error) {
        console.error('Error downloading report:', error);
        this.$toast.error('Failed to download report');
      } finally {
        this.isDownloadingReport = false;
      }
    }
  }
}
</script>

<style scoped>
.evaluations-list-view {
  padding: 0;
}

.page-header-card {
  background: linear-gradient(135deg, var(--v-primary-darken1, #016b4b) 0%, var(--v-primary-lighten1, #016b4b) 100%);
  color: white;
}

.page-header-card .v-card__title {
  color: white;
}

.page-header-card .v-card__text {
  color: rgba(255, 255, 255, 0.9);
}

.active-filter {
  background-color: rgba(25, 118, 210, 0.1) !important;
  border-color: #1976d2 !important;
}

.search-field {
  max-width: 400px;
}

.actions-row {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.evaluations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.evaluation-card {
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.on-hover {
  transform: translateY(-4px);
}

.status-indicator {
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
}

.status-draft {
  background-color: #9e9e9e;
}

.status-in-progress {
  background-color: #2196f3;
}

.status-submitted {
  background-color: #ff9800;
}

.status-reviewed {
  background-color: #9c27b0;
}

.status-approved {
  background-color: #4caf50;
}

.status-rejected {
  background-color: #f44336;
}

.evaluation-title {
  padding-left: 8px;
  font-size: 1.1rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.evaluation-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-item {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  line-height: 1.2;
}

.detail-label {
  color: rgba(0, 0, 0, 0.6);
  margin-right: 8px;
  min-width: 70px;
}

.detail-value {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.8);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.date-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 960px) {
  .evaluations-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
}

@media (max-width: 600px) {
  .evaluations-grid {
    grid-template-columns: 1fr;
  }

  .actions-row {
    flex-direction: column;
    align-items: stretch;
  }

  .search-field {
    max-width: 100%;
    margin-bottom: 16px;
  }

  .date-fields {
    grid-template-columns: 1fr;
  }
}
</style>
